import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import React, { useEffect, useRef, useState } from 'react';
import { ICompanyGroupOption, ICompanyUserGroup } from '../../../../shared/user/dto/ICompanyUserGroup';
import { SelectedOptionsMap, useGroupOptions } from '../../../../../shared/hooks/useGroupOptions';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import Chip from '../../../../../shared/components/chip/Chip';
import { Button, Col, Row } from 'react-bootstrap';
import { BriefingService } from '../service';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { EditIcon } from '../../../../../shared/Icons';

interface SelectGroupSectionProps {
    showEditIcon?: boolean;
}
function SelectGroupSection({ showEditIcon }: SelectGroupSectionProps) {
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>();
    const locale = useSelector((state: IAllStates) => state.auth.user.locale);
    const isDe = locale === 'de';
    const [allGroups, setAllGroups] = useState<ICompanyUserGroup[]>([]);
    const [isEditMode, setEditMode] = useState(false);
    const {
        selectedOptionsMap,
        autocompleteKeys,
        flatOptionIds,
        getAvailableOptions,
        handleOptionSelect,
        handleOptionRemove,
        initSelectedOptions,
        buildInitialMap,
        resetOptions,
    } = useGroupOptions();
    const initialOptionsMapRef = useRef<SelectedOptionsMap>({});

    const getOptionLabel = (option: ICompanyGroupOption) =>
        isDe && option?.labelDe?.trim() ? option.labelDe : option?.label;
    const getGroupName = (group: ICompanyUserGroup) => (isDe && group?.nameDe?.trim() ? group.nameDe : group?.name);

    useEffect(() => {
        BriefingService.getCampaignGroups(id)
            .then(response => {
                setAllGroups(response);
                initSelectedOptions(response);
                initialOptionsMapRef.current = buildInitialMap(response);
            })
            .catch(err => ErrorToast(err));
    }, [id]);

    const saveGroups = () => {
        BriefingService.addCampaignGroups({ optionIds: flatOptionIds }, id)
            .then(response => {
                setEditMode(false);
                SuccessToast('Successfully saved options');
                initialOptionsMapRef.current = buildInitialMap(response);
            })
            .catch(err => ErrorToast(err));
    };

    function handleCancel() {
        resetOptions(initialOptionsMapRef.current);
        setEditMode(false);
    }
    if (allGroups.length === 0) return null;

    return (
        <CardLayout classCard="mb-3">
            <div className="d-flex justify-content-between align-items-center">
                <div>
                    <h5>{t('brand.campaign.briefing.group')}</h5>
                    <p className="text-muted">{t('brand.campaign.briefing.group_description')}</p>
                </div>
                {!isEditMode && showEditIcon && (
                    <TooltipOverlay content={t('brand.campaign.briefing.edit_section')}>
                        <EditIcon id="edit-icon" onClick={() => setEditMode(true)} />
                    </TooltipOverlay>
                )}
            </div>

            <Row>
                {allGroups.map(group => {
                    const selectedForGroup = selectedOptionsMap[group.id] ?? [];
                    const availableOptions = getAvailableOptions(group);
                    const localizedOptions = availableOptions.map(option => ({
                        ...option,
                        label: getOptionLabel(option),
                    }));

                    return (
                        <Col xl={allGroups.length > 2 ? 4 : 6} md={6} className="mb-4" key={group.id}>
                            <h6>
                                {getGroupName(group)}
                                {group.isMultiple && <span className="text-muted"> (multiple select)</span>}
                            </h6>
                            <CustomAutocomplete
                                isDisabled={!isEditMode}
                                key={autocompleteKeys[group.id] ?? 0}
                                dataArray={localizedOptions}
                                isWithImage={false}
                                inputLabel="options"
                                isMultiple={false}
                                queryFilterKey={`group-${group.id}`}
                                inputLabelTranslation={`Search by ${getGroupName(group)}`}
                                handleChangeParams={(_key, paramValue) => {
                                    if (!paramValue) return;
                                    const selectedId = (paramValue as unknown as ICompanyGroupOption)?.id;
                                    const originalOption = availableOptions.find(option => option.id === selectedId);
                                    if (originalOption) handleOptionSelect(group, originalOption);
                                }}
                            />
                            {selectedForGroup.length > 0 && (
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    {selectedForGroup.map(option => (
                                        <Chip
                                            key={option.id}
                                            label={getOptionLabel(option)}
                                            onDelete={() => handleOptionRemove(group.id, option.id)}
                                            isActive={true}
                                            disabled={!isEditMode}
                                        />
                                    ))}
                                </div>
                            )}
                        </Col>
                    );
                })}
            </Row>
            <div className="d-flex justify-content-between mt-3">
                {isEditMode && (
                    <Button variant="outline-primary" className="btn-sm" onClick={handleCancel}>
                        {t('general.button.cancel')}
                    </Button>
                )}
                {isEditMode && (
                    <Button variant="primary" className="btn-sm" onClick={saveGroups}>
                        {t('general.button.save')}
                    </Button>
                )}
            </div>
        </CardLayout>
    );
}

export default SelectGroupSection;
